import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  ArrowRight, 
  Building2, 
  DollarSign, 
  FileText, 
  CheckCircle,
  Loader2,
  Upload,
  MapPin,
  Users,
  Shield,
  CreditCard,
  User,
  UserCircle,
  File,
  X,
  AlertCircle,
  Home,
  Wallet,
  FileCheck,
  Zap,
  Briefcase
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_small-biz-loans/artifacts/ojg09jbh_logo.png";

const LOAN_TYPES = [
  { id: "working_capital", name: "Working Capital" },
  { id: "invoice_discounting", name: "Invoice Discounting" },
  { id: "merchant_cash_advance", name: "Merchant Cash Advance" },
  { id: "wholesale_lending", name: "Microfinance Wholesale Lending" },
  { id: "term_loan", name: "Term Loan" },
  { id: "home_equity", name: "Home Equity Loans" },
  { id: "device_finance", name: "Device Finance" }
];

const BUSINESS_TYPES = [
  "Retail",
  "Restaurant/Food Service",
  "Manufacturing",
  "Professional Services",
  "Healthcare",
  "Construction",
  "Technology",
  "Transportation",
  "Agriculture",
  "Other"
];

const SECURITY_TYPES = [
  "Property/Real Estate",
  "Equipment/Machinery",
  "Inventory",
  "Accounts Receivable",
  "Personal Guarantee",
  "Vehicle",
  "None/Unsecured"
];

const DOCUMENT_CATEGORIES = [
  { id: "identification", name: "Identification", description: "National ID, Passport, Driver's License", icon: "id", required: true },
  { id: "proof_of_address", name: "Proof of Address", description: "Utility bill, Lease agreement, Bank statement", icon: "address", required: true },
  { id: "proof_of_income", name: "Proof of Income", description: "Payslips, Employment letter, Tax returns", icon: "income", required: false },
  { id: "bank_statement", name: "Bank Statement", description: "Last 3-6 months bank statements", icon: "bank", required: true },
  { id: "surety", name: "Surety Documents", description: "Guarantor documents, Collateral proof", icon: "surety", required: false },
  { id: "utility_bills", name: "Utility Bills", description: "Electricity, Water, Internet bills", icon: "utility", required: false },
  { id: "company_documents", name: "Company Documents", description: "Registration, License, Tax clearance", icon: "company", required: false }
];

const LoanApplication = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 = applicant type selection
  const [loading, setLoading] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [applicantType, setApplicantType] = useState(""); // "individual" or "business"
  
  // Individual form data
  const [individualData, setIndividualData] = useState({
    full_name: user?.name || "",
    id_number: "",
    individual_address: "",
    loan_type: "",
    amount: "",
    term_months: "",
    purpose: "",
    guarantor_name: "",
    guarantor_id: "",
    guarantor_phone: "",
    guarantor_address: "",
    security_type: "",
    security_description: ""
  });

  // Business form data
  const [businessData, setBusinessData] = useState({
    loan_type: "",
    amount: "",
    term_months: "",
    purpose: "",
    security_type: "",
    security_description: "",
    business_name: user?.business_name || "",
    business_type: "",
    license_number: "",
    business_address: "",
    city: "",
    state: "",
    postal_code: "",
    directors: "",
    years_in_business: "",
    annual_revenue: "",
    monthly_expenses: "",
    existing_debt: "",
    employees_count: ""
  });

  const [alternativeData, setAlternativeData] = useState({
    bank_statement: { summary: "", metrics: {} },
    mobile_money: { summary: "", metrics: {} },
    payment_history: { summary: "", metrics: {} }
  });

  const [documents, setDocuments] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null);

  const getTotalSteps = () => applicantType === "individual" ? 5 : 6; // Added document upload step
  const progress = step === 0 ? 0 : (step / getTotalSteps()) * 100;

  const handleIndividualChange = (field, value) => {
    setIndividualData(prev => ({ ...prev, [field]: value }));
  };

  const handleBusinessChange = (field, value) => {
    setBusinessData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = () => {
    if (applicantType === "individual") {
      switch (step) {
        case 1:
          if (!individualData.full_name || !individualData.id_number || !individualData.individual_address) {
            toast.error("Please fill in all required fields");
            return false;
          }
          break;
        case 2:
          if (!individualData.loan_type || !individualData.amount || !individualData.term_months) {
            toast.error("Please fill in all required fields");
            return false;
          }
          if (parseFloat(individualData.amount) < 500) {
            toast.error("Minimum loan amount is $500");
            return false;
          }
          break;
        default:
          break;
      }
    } else {
      switch (step) {
        case 1:
          if (!businessData.loan_type || !businessData.amount || !businessData.term_months || !businessData.purpose) {
            toast.error("Please fill in all required fields");
            return false;
          }
          if (parseFloat(businessData.amount) < 500) {
            toast.error("Minimum loan amount is $500");
            return false;
          }
          break;
        case 2:
          if (!businessData.business_name || !businessData.business_type || !businessData.license_number || 
              !businessData.business_address || !businessData.city) {
            toast.error("Please fill in all required fields");
            return false;
          }
          break;
        case 3:
          if (!businessData.directors || !businessData.years_in_business || !businessData.annual_revenue || 
              !businessData.monthly_expenses || !businessData.employees_count) {
            toast.error("Please fill in all required fields");
            return false;
          }
          break;
        default:
          break;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (step === 1) {
      setStep(0);
      setApplicantType("");
    } else {
      setStep(prev => prev - 1);
    }
  };

  const handleFileUpload = async (categoryId, file) => {
    if (!file) return;
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'application/msword', 
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload PDF, Word, or image files only");
      return;
    }
    
    setUploadingDoc(categoryId);
    
    try {
      // Convert file to base64 for storage
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Data = reader.result;
        setDocuments(prev => ({
          ...prev,
          [categoryId]: {
            name: file.name,
            size: file.size,
            type: file.type,
            data: base64Data,
            uploadedAt: new Date().toISOString()
          }
        }));
        toast.success(`${file.name} uploaded successfully`);
        setUploadingDoc(null);
      };
      reader.onerror = () => {
        toast.error("Failed to read file");
        setUploadingDoc(null);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Failed to upload document");
      setUploadingDoc(null);
    }
  };

  const handleRemoveDocument = (categoryId) => {
    setDocuments(prev => {
      const updated = { ...prev };
      delete updated[categoryId];
      return updated;
    });
    toast.info("Document removed");
  };

  const getDocumentIcon = (iconType) => {
    switch (iconType) {
      case 'id': return <CreditCard className="h-5 w-5" />;
      case 'address': return <Home className="h-5 w-5" />;
      case 'income': return <Wallet className="h-5 w-5" />;
      case 'bank': return <Building2 className="h-5 w-5" />;
      case 'surety': return <FileCheck className="h-5 w-5" />;
      case 'utility': return <Zap className="h-5 w-5" />;
      case 'company': return <Briefcase className="h-5 w-5" />;
      default: return <FileText className="h-5 w-5" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleApplicantTypeSelect = (type) => {
    setApplicantType(type);
    setStep(1);
  };

  const handleSubmitApplication = async () => {
    setLoading(true);
    try {
      let payload;
      
      if (applicantType === "individual") {
        payload = {
          applicant_type: "individual",
          full_name: individualData.full_name,
          id_number: individualData.id_number,
          individual_address: individualData.individual_address,
          loan_type: individualData.loan_type,
          amount: parseFloat(individualData.amount),
          term_months: parseInt(individualData.term_months),
          purpose: individualData.purpose || "Personal loan",
          guarantor_name: individualData.guarantor_name,
          guarantor_id: individualData.guarantor_id,
          guarantor_phone: individualData.guarantor_phone,
          guarantor_address: individualData.guarantor_address,
          security_type: individualData.security_type,
          security_description: individualData.security_description
        };
      } else {
        payload = {
          applicant_type: "business",
          loan_type: businessData.loan_type,
          amount: parseFloat(businessData.amount),
          term_months: parseInt(businessData.term_months),
          purpose: businessData.purpose,
          security_type: businessData.security_type,
          security_description: businessData.security_description,
          business_name: businessData.business_name,
          business_type: businessData.business_type,
          license_number: businessData.license_number,
          business_address: businessData.business_address,
          city: businessData.city,
          state: businessData.state,
          postal_code: businessData.postal_code,
          directors: businessData.directors,
          years_in_business: parseInt(businessData.years_in_business),
          annual_revenue: parseFloat(businessData.annual_revenue),
          monthly_expenses: parseFloat(businessData.monthly_expenses),
          existing_debt: parseFloat(businessData.existing_debt || 0),
          employees_count: parseInt(businessData.employees_count)
        };
      }
      
      // Add documents metadata to payload
      const documentsMeta = {};
      Object.keys(documents).forEach(key => {
        documentsMeta[key] = {
          name: documents[key].name,
          size: documents[key].size,
          type: documents[key].type,
          uploadedAt: documents[key].uploadedAt
        };
      });
      payload.documents = documentsMeta;
      
      const response = await axios.post(
        `${API}/loans/apply`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const appId = response.data.application_id;
      setApplicationId(appId);
      
      // Upload documents to backend
      if (Object.keys(documents).length > 0) {
        try {
          await axios.post(
            `${API}/documents/upload`,
            {
              application_id: appId,
              documents: documents
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (docError) {
          console.error("Document upload error:", docError);
          toast.warning("Application submitted, but some documents may not have been uploaded");
        }
      }
      
      toast.success("Application submitted successfully!");
      setStep(getTotalSteps());
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAlternativeData = async (dataType) => {
    if (!applicationId) return;
    
    const data = alternativeData[dataType];
    if (!data.summary) {
      toast.error("Please provide a summary");
      return;
    }

    try {
      await axios.post(
        `${API}/alternative-data/submit`,
        {
          application_id: applicationId,
          data_type: dataType,
          data_summary: data.summary,
          metrics: data.metrics
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${dataType.replace('_', ' ')} data submitted!`);
    } catch (error) {
      toast.error("Failed to submit alternative data");
    }
  };

  // Render document upload step (shared between Individual and Business)
  const renderDocumentUploadStep = () => {
    const requiredDocs = applicantType === "business" 
      ? DOCUMENT_CATEGORIES 
      : DOCUMENT_CATEGORIES.filter(c => c.id !== "company_documents");
    
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-lg font-semibold text-[#0A2540]">Upload Documents</h3>
          <p className="text-gray-500 text-sm">Upload supporting documents to strengthen your application</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800 font-medium">Accepted file formats</p>
              <p className="text-sm text-blue-700">PDF, Word documents, and images (JPG, PNG) up to 10MB each</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {requiredDocs.map((category) => (
            <div 
              key={category.id} 
              className={`border rounded-lg p-4 transition-all ${
                documents[category.id] 
                  ? 'border-emerald-300 bg-emerald-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${
                    documents[category.id] 
                      ? 'bg-emerald-100 text-emerald-600' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {getDocumentIcon(category.icon)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-[#0A2540]">{category.name}</h4>
                      {category.required && (
                        <span className="text-xs text-red-500">*Required</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{category.description}</p>
                    
                    {documents[category.id] && (
                      <div className="mt-2 flex items-center gap-2 text-sm text-emerald-700">
                        <File className="h-4 w-4" />
                        <span className="truncate max-w-[200px]">{documents[category.id].name}</span>
                        <span className="text-emerald-600">({formatFileSize(documents[category.id].size)})</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {documents[category.id] ? (
                    <>
                      <span className="flex items-center gap-1 text-sm text-emerald-600">
                        <CheckCircle className="h-4 w-4" />
                        Uploaded
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleRemoveDocument(category.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileUpload(category.id, e.target.files?.[0])}
                        disabled={uploadingDoc === category.id}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="pointer-events-none"
                        disabled={uploadingDoc === category.id}
                      >
                        {uploadingDoc === category.id ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Upload className="h-4 w-4 mr-2" />
                        )}
                        Upload
                      </Button>
                    </label>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Documents Uploaded</p>
              <p className="text-xs text-gray-500">
                {Object.keys(documents).length} of {requiredDocs.length} documents
              </p>
            </div>
            <div className="flex items-center gap-1">
              {requiredDocs.map((cat) => (
                <div
                  key={cat.id}
                  className={`w-3 h-3 rounded-full ${
                    documents[cat.id] ? 'bg-emerald-500' : 'bg-gray-300'
                  }`}
                  title={cat.name}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render applicant type selection
  const renderApplicantTypeSelection = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold text-[#0A2540]">Select Applicant Type</h3>
        <p className="text-gray-500 mt-2">Choose the type of loan application you want to submit</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card 
          className={`cursor-pointer transition-all hover:border-[#0A2540] hover:shadow-lg ${applicantType === 'individual' ? 'border-2 border-[#0A2540] bg-[#0A2540]/5' : ''}`}
          onClick={() => handleApplicantTypeSelect("individual")}
          data-testid="individual-option"
        >
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-[#0A2540]/10 flex items-center justify-center mx-auto mb-4">
              <User className="h-10 w-10 text-[#0A2540]" />
            </div>
            <h4 className="text-xl font-semibold text-[#0A2540] mb-2">Individual</h4>
            <p className="text-gray-500 text-sm">Personal loan application for individuals</p>
            <ul className="text-left text-sm text-gray-600 mt-4 space-y-1">
              <li>• Personal identification</li>
              <li>• Guarantor information</li>
              <li>• Collateral details</li>
            </ul>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:border-[#0A2540] hover:shadow-lg ${applicantType === 'business' ? 'border-2 border-[#0A2540] bg-[#0A2540]/5' : ''}`}
          onClick={() => handleApplicantTypeSelect("business")}
          data-testid="business-option"
        >
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Building2 className="h-10 w-10 text-amber-600" />
            </div>
            <h4 className="text-xl font-semibold text-[#0A2540] mb-2">Business</h4>
            <p className="text-gray-500 text-sm">Business loan application for companies</p>
            <ul className="text-left text-sm text-gray-600 mt-4 space-y-1">
              <li>• Business registration details</li>
              <li>• Directors information</li>
              <li>• Financial statements</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // Render Individual Form Steps
  const renderIndividualStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="full_name"
                  placeholder="Enter your full name"
                  className="pl-10 h-11"
                  value={individualData.full_name}
                  onChange={(e) => handleIndividualChange("full_name", e.target.value)}
                  data-testid="full-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="id_number">ID Number *</Label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="id_number"
                  placeholder="National ID or Passport number"
                  className="pl-10 h-11"
                  value={individualData.id_number}
                  onChange={(e) => handleIndividualChange("id_number", e.target.value)}
                  data-testid="id-number-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="individual_address">Address *</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Textarea
                  id="individual_address"
                  placeholder="Enter your full residential address"
                  className="pl-10 min-h-[80px]"
                  value={individualData.individual_address}
                  onChange={(e) => handleIndividualChange("individual_address", e.target.value)}
                  data-testid="address-input"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="loan_type">Loan Type *</Label>
              <Select
                value={individualData.loan_type}
                onValueChange={(value) => handleIndividualChange("loan_type", value)}
              >
                <SelectTrigger className="h-11" data-testid="loan-type-select">
                  <SelectValue placeholder="Select loan type" />
                </SelectTrigger>
                <SelectContent>
                  {LOAN_TYPES.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount Requested ($) *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="amount"
                    type="number"
                    placeholder="5,000"
                    className="pl-10 h-11"
                    value={individualData.amount}
                    onChange={(e) => handleIndividualChange("amount", e.target.value)}
                    data-testid="amount-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="term_months">Tenure (months) *</Label>
                <Select
                  value={individualData.term_months}
                  onValueChange={(value) => handleIndividualChange("term_months", value)}
                >
                  <SelectTrigger className="h-11" data-testid="term-select">
                    <SelectValue placeholder="Select tenure" />
                  </SelectTrigger>
                  <SelectContent>
                    {[3, 6, 12, 18, 24, 36, 48, 60].map((months) => (
                      <SelectItem key={months} value={months.toString()}>
                        {months} months
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="purpose">Loan Purpose</Label>
              <Textarea
                id="purpose"
                placeholder="What will you use this loan for?"
                className="min-h-[60px]"
                value={individualData.purpose}
                onChange={(e) => handleIndividualChange("purpose", e.target.value)}
                data-testid="purpose-input"
              />
            </div>

            <div className="pt-4 border-t">
              <h4 className="font-semibold text-[#0A2540] mb-4 flex items-center gap-2">
                <UserCircle className="h-5 w-5" />
                Guarantor Information
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="guarantor_name">Guarantor Name</Label>
                  <Input
                    id="guarantor_name"
                    placeholder="Full name of guarantor"
                    className="h-11"
                    value={individualData.guarantor_name}
                    onChange={(e) => handleIndividualChange("guarantor_name", e.target.value)}
                    data-testid="guarantor-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="guarantor_id">Guarantor ID Number</Label>
                  <Input
                    id="guarantor_id"
                    placeholder="ID number"
                    className="h-11"
                    value={individualData.guarantor_id}
                    onChange={(e) => handleIndividualChange("guarantor_id", e.target.value)}
                    data-testid="guarantor-id-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="guarantor_phone">Guarantor Phone</Label>
                  <Input
                    id="guarantor_phone"
                    placeholder="Phone number"
                    className="h-11"
                    value={individualData.guarantor_phone}
                    onChange={(e) => handleIndividualChange("guarantor_phone", e.target.value)}
                    data-testid="guarantor-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="guarantor_address">Guarantor Address</Label>
                  <Input
                    id="guarantor_address"
                    placeholder="Address"
                    className="h-11"
                    value={individualData.guarantor_address}
                    onChange={(e) => handleIndividualChange("guarantor_address", e.target.value)}
                    data-testid="guarantor-address-input"
                  />
                </div>
              </div>
            </div>

            <div className="pt-4 border-t">
              <h4 className="font-semibold text-[#0A2540] mb-4 flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Collateral Information
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="security_type">Collateral Type</Label>
                  <Select
                    value={individualData.security_type}
                    onValueChange={(value) => handleIndividualChange("security_type", value)}
                  >
                    <SelectTrigger className="h-11" data-testid="security-type-select">
                      <SelectValue placeholder="Select collateral type" />
                    </SelectTrigger>
                    <SelectContent>
                      {SECURITY_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="security_description">Collateral Description</Label>
                  <Input
                    id="security_description"
                    placeholder="Describe the collateral"
                    className="h-11"
                    value={individualData.security_description}
                    onChange={(e) => handleIndividualChange("security_description", e.target.value)}
                    data-testid="security-description-input"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return renderDocumentUploadStep();

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-[#0A2540]">Review Your Application</h3>
              <p className="text-gray-500 text-sm">Please review your information before submitting</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="bg-gray-50 border-0">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">Personal Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Full Name</span>
                    <span className="font-medium">{individualData.full_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">ID Number</span>
                    <span className="font-medium">{individualData.id_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Address</span>
                    <span className="font-medium text-right max-w-[200px] truncate">{individualData.individual_address}</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-50 border-0">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">Loan Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Type</span>
                    <span className="font-medium">{LOAN_TYPES.find(t => t.id === individualData.loan_type)?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount</span>
                    <span className="font-mono font-semibold">${parseFloat(individualData.amount || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tenure</span>
                    <span className="font-medium">{individualData.term_months} months</span>
                  </div>
                </CardContent>
              </Card>

              {individualData.guarantor_name && (
                <Card className="bg-gray-50 border-0">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Guarantor</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Name</span>
                      <span className="font-medium">{individualData.guarantor_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Phone</span>
                      <span className="font-medium">{individualData.guarantor_phone || '-'}</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {individualData.security_type && (
                <Card className="bg-gray-50 border-0">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Collateral</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Type</span>
                      <span className="font-medium">{individualData.security_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Description</span>
                      <span className="font-medium">{individualData.security_description || '-'}</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {Object.keys(documents).length > 0 && (
                <Card className="bg-gray-50 border-0 md:col-span-2">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Uploaded Documents</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {Object.keys(documents).map(key => (
                        <span key={key} className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs">
                          <CheckCircle className="h-3 w-3" />
                          {DOCUMENT_CATEGORIES.find(c => c.id === key)?.name}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Render Business Form Steps
  const renderBusinessStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="loan_type">Loan Type *</Label>
              <Select
                value={businessData.loan_type}
                onValueChange={(value) => handleBusinessChange("loan_type", value)}
              >
                <SelectTrigger className="h-11" data-testid="loan-type-select">
                  <SelectValue placeholder="Select loan type" />
                </SelectTrigger>
                <SelectContent>
                  {LOAN_TYPES.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount Requested ($) *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="amount"
                    type="number"
                    placeholder="10,000"
                    className="pl-10 h-11"
                    value={businessData.amount}
                    onChange={(e) => handleBusinessChange("amount", e.target.value)}
                    data-testid="loan-amount-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="term_months">Loan Term (months) *</Label>
                <Select
                  value={businessData.term_months}
                  onValueChange={(value) => handleBusinessChange("term_months", value)}
                >
                  <SelectTrigger className="h-11" data-testid="term-select">
                    <SelectValue placeholder="Select term" />
                  </SelectTrigger>
                  <SelectContent>
                    {[3, 6, 12, 18, 24, 36, 48, 60, 84].map((months) => (
                      <SelectItem key={months} value={months.toString()}>
                        {months} months
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="purpose">Loan Purpose *</Label>
              <Textarea
                id="purpose"
                placeholder="Describe how you plan to use this loan..."
                className="min-h-[80px]"
                value={businessData.purpose}
                onChange={(e) => handleBusinessChange("purpose", e.target.value)}
                data-testid="loan-purpose-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="security_type">Security/Collateral Type</Label>
                <Select
                  value={businessData.security_type}
                  onValueChange={(value) => handleBusinessChange("security_type", value)}
                >
                  <SelectTrigger className="h-11" data-testid="security-type-select">
                    <SelectValue placeholder="Select security type" />
                  </SelectTrigger>
                  <SelectContent>
                    {SECURITY_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="security_description">Security Description</Label>
                <Input
                  id="security_description"
                  placeholder="Describe the collateral..."
                  className="h-11"
                  value={businessData.security_description}
                  onChange={(e) => handleBusinessChange("security_description", e.target.value)}
                  data-testid="security-description-input"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="business_name">Business Name *</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="business_name"
                    placeholder="Your Business Name"
                    className="pl-10 h-11"
                    value={businessData.business_name}
                    onChange={(e) => handleBusinessChange("business_name", e.target.value)}
                    data-testid="business-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="license_number">Business License Number *</Label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="license_number"
                    placeholder="e.g., BL-123456789"
                    className="pl-10 h-11"
                    value={businessData.license_number}
                    onChange={(e) => handleBusinessChange("license_number", e.target.value)}
                    data-testid="license-number-input"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="business_type">Business Type *</Label>
              <Select
                value={businessData.business_type}
                onValueChange={(value) => handleBusinessChange("business_type", value)}
              >
                <SelectTrigger className="h-11" data-testid="business-type-select">
                  <SelectValue placeholder="Select business type" />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="business_address">Business Address *</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="business_address"
                  placeholder="Street address"
                  className="pl-10 h-11"
                  value={businessData.business_address}
                  onChange={(e) => handleBusinessChange("business_address", e.target.value)}
                  data-testid="business-address-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  placeholder="City"
                  className="h-11"
                  value={businessData.city}
                  onChange={(e) => handleBusinessChange("city", e.target.value)}
                  data-testid="city-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="state">State/Province</Label>
                <Input
                  id="state"
                  placeholder="State"
                  className="h-11"
                  value={businessData.state}
                  onChange={(e) => handleBusinessChange("state", e.target.value)}
                  data-testid="state-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="postal_code">Postal Code</Label>
                <Input
                  id="postal_code"
                  placeholder="Postal code"
                  className="h-11"
                  value={businessData.postal_code}
                  onChange={(e) => handleBusinessChange("postal_code", e.target.value)}
                  data-testid="postal-code-input"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="directors">Directors/Owners *</Label>
              <div className="relative">
                <Users className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Textarea
                  id="directors"
                  placeholder="List all directors/owners with their names and positions"
                  className="pl-10 min-h-[80px]"
                  value={businessData.directors}
                  onChange={(e) => handleBusinessChange("directors", e.target.value)}
                  data-testid="directors-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="years_in_business">Years in Business *</Label>
                <Input
                  id="years_in_business"
                  type="number"
                  placeholder="5"
                  className="h-11"
                  value={businessData.years_in_business}
                  onChange={(e) => handleBusinessChange("years_in_business", e.target.value)}
                  data-testid="years-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="employees_count">Number of Employees *</Label>
                <Input
                  id="employees_count"
                  type="number"
                  placeholder="10"
                  className="h-11"
                  value={businessData.employees_count}
                  onChange={(e) => handleBusinessChange("employees_count", e.target.value)}
                  data-testid="employees-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="annual_revenue">Annual Revenue ($) *</Label>
                <Input
                  id="annual_revenue"
                  type="number"
                  placeholder="500,000"
                  className="h-11"
                  value={businessData.annual_revenue}
                  onChange={(e) => handleBusinessChange("annual_revenue", e.target.value)}
                  data-testid="revenue-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="monthly_expenses">Monthly Expenses ($) *</Label>
                <Input
                  id="monthly_expenses"
                  type="number"
                  placeholder="30,000"
                  className="h-11"
                  value={businessData.monthly_expenses}
                  onChange={(e) => handleBusinessChange("monthly_expenses", e.target.value)}
                  data-testid="expenses-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="existing_debt">Existing Debt ($)</Label>
              <Input
                id="existing_debt"
                type="number"
                placeholder="0"
                className="h-11"
                value={businessData.existing_debt}
                onChange={(e) => handleBusinessChange("existing_debt", e.target.value)}
                data-testid="debt-input"
              />
            </div>
          </div>
        );

      case 4:
        return renderDocumentUploadStep();

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-[#0A2540]">Review Your Application</h3>
              <p className="text-gray-500 text-sm">Please review your information before submitting</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="bg-gray-50 border-0">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">Loan Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Type</span>
                    <span className="font-medium">{LOAN_TYPES.find(t => t.id === businessData.loan_type)?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount</span>
                    <span className="font-mono font-semibold">${parseFloat(businessData.amount || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Term</span>
                    <span className="font-medium">{businessData.term_months} months</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Security</span>
                    <span className="font-medium">{businessData.security_type || "None"}</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-50 border-0">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">Business Info</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Name</span>
                    <span className="font-medium">{businessData.business_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">License #</span>
                    <span className="font-medium">{businessData.license_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Type</span>
                    <span className="font-medium">{businessData.business_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Location</span>
                    <span className="font-medium">{businessData.city}, {businessData.state}</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-50 border-0">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">Directors</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700">{businessData.directors}</p>
                </CardContent>
              </Card>

              <Card className="bg-gray-50 border-0">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">Financials</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Revenue</span>
                    <span className="font-mono font-semibold">${parseFloat(businessData.annual_revenue || 0).toLocaleString()}/yr</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Expenses</span>
                    <span className="font-mono">${parseFloat(businessData.monthly_expenses || 0).toLocaleString()}/mo</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Years</span>
                    <span className="font-medium">{businessData.years_in_business}</span>
                  </div>
                </CardContent>
              </Card>

              {Object.keys(documents).length > 0 && (
                <Card className="bg-gray-50 border-0 md:col-span-2">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Uploaded Documents</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {Object.keys(documents).map(key => (
                        <span key={key} className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs">
                          <CheckCircle className="h-3 w-3" />
                          {DOCUMENT_CATEGORIES.find(c => c.id === key)?.name}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> After submitting, you can add alternative data to strengthen your application.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Render completion step (same for both)
  const renderCompletionStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-8 w-8 text-emerald-600" />
        </div>
        <h3 className="text-xl font-semibold text-[#0A2540]">Application Submitted!</h3>
        <p className="text-gray-500 mt-2">
          Your application ID: <span className="font-mono font-semibold">{applicationId}</span>
        </p>
      </div>

      <div className="border-t pt-6">
        <h4 className="font-semibold text-[#0A2540] mb-4">Strengthen Your Application</h4>
        <p className="text-gray-600 text-sm mb-4">
          Submit alternative data to help us better assess your application.
        </p>

        <div className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <FileText className="h-5 w-5 text-[#0A2540]" />
                <span className="font-medium">Bank Statement Summary</span>
              </div>
              <Textarea
                placeholder="Summarize your bank statement data..."
                className="mb-2"
                value={alternativeData.bank_statement.summary}
                onChange={(e) => setAlternativeData(prev => ({
                  ...prev,
                  bank_statement: { ...prev.bank_statement, summary: e.target.value }
                }))}
              />
              <Button size="sm" onClick={() => handleSubmitAlternativeData("bank_statement")}>
                Submit
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <FileText className="h-5 w-5 text-[#0A2540]" />
                <span className="font-medium">Mobile Money Statement</span>
              </div>
              <Textarea
                placeholder="Summarize your mobile money transactions..."
                className="mb-2"
                value={alternativeData.mobile_money.summary}
                onChange={(e) => setAlternativeData(prev => ({
                  ...prev,
                  mobile_money: { ...prev.mobile_money, summary: e.target.value }
                }))}
              />
              <Button size="sm" onClick={() => handleSubmitAlternativeData("mobile_money")}>
                Submit
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="flex justify-center pt-4">
        <Link to="/dashboard">
          <Button className="bg-[#0A2540]" data-testid="go-to-dashboard-btn">
            Go to Dashboard
          </Button>
        </Link>
      </div>
    </div>
  );

  const renderStep = () => {
    if (step === 0) return renderApplicantTypeSelection();
    if (step === getTotalSteps()) return renderCompletionStep();
    
    if (applicantType === "individual") {
      return renderIndividualStep();
    } else {
      return renderBusinessStep();
    }
  };

  const getStepTitle = () => {
    if (step === 0) return "Applicant Type";
    if (step === getTotalSteps()) return "Application Submitted";
    
    if (applicantType === "individual") {
      switch (step) {
        case 1: return "Personal Information";
        case 2: return "Loan & Guarantor Details";
        case 3: return "Upload Documents";
        case 4: return "Review Application";
        default: return "";
      }
    } else {
      switch (step) {
        case 1: return "Loan Details & Security";
        case 2: return "Business Information";
        case 3: return "Directors & Financials";
        case 4: return "Upload Documents";
        case 5: return "Review Application";
        default: return "";
      }
    }
  };

  const getStepDescription = () => {
    if (step === 0) return "Choose how you want to apply";
    if (step === getTotalSteps()) return "Your application is being processed";
    
    if (applicantType === "individual") {
      switch (step) {
        case 1: return "Enter your personal details";
        case 2: return "Loan amount, tenure, guarantor and collateral";
        case 3: return "Upload supporting documents";
        case 4: return "Make sure everything looks correct";
        default: return "";
      }
    } else {
      switch (step) {
        case 1: return "Tell us about the loan you need";
        case 2: return "Business details and address";
        case 3: return "Directors and financial information";
        case 4: return "Upload supporting documents";
        case 5: return "Make sure everything looks correct";
        default: return "";
      }
    }
  };

  const isReviewStep = () => {
    if (applicantType === "individual") return step === 4;
    return step === 5;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-gray-600 hover:text-[#0A2540]">
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Link>
            <img src={LOGO_URL} alt="Tzur Finances" className="h-8" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress */}
        {step > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[#0A2540]">
                Step {step} of {getTotalSteps()}
              </span>
              <span className="text-sm text-gray-500">
                {applicantType === "individual" ? "Individual" : "Business"} Application
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}

        {/* Form Card */}
        <Card className="bg-white" data-testid="loan-application-form">
          <CardHeader>
            <CardTitle className="text-2xl text-[#0A2540]">{getStepTitle()}</CardTitle>
            <CardDescription>{getStepDescription()}</CardDescription>
          </CardHeader>
          <CardContent>
            {renderStep()}

            {/* Navigation Buttons */}
            {step > 0 && step < getTotalSteps() && (
              <div className="flex justify-between mt-8 pt-6 border-t">
                <Button variant="outline" onClick={handleBack} data-testid="back-btn">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>

                {!isReviewStep() ? (
                  <Button className="bg-[#0A2540]" onClick={handleNext} data-testid="next-btn">
                    Continue
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button 
                    className="bg-[#0A2540]" 
                    onClick={handleSubmitApplication}
                    disabled={loading}
                    data-testid="submit-application-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Submit Application
                        <CheckCircle className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default LoanApplication;
